// src/components/About.js
import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

function About() {
  const AboutStyling = {
    general: {
      color: '#2C3E50',
      fontFamily: "'Inter', sans-serif",
    },
    hero: {
      position: 'relative',
      marginBottom: '3rem',
      borderRadius: '12px',
      overflow: 'hidden',
    },
    heroOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.7))',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      color: 'white',
      textAlign: 'center',
      padding: '2rem',
    },
    heroTitle: {
      fontSize: '3rem',
      fontWeight: '700',
      marginBottom: '1rem',
      textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
    },
    heroSubtitle: {
      fontSize: '1.2rem',
      maxWidth: '800px',
      lineHeight: '1.6',
      textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
    },
    section: {
      marginBottom: '3rem',
    },
    h1: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '1.5rem',
      color: '#1a365d',
    },
    h2: {
      fontSize: '1.8rem',
      fontWeight: '600',
      marginTop: '2.5rem',
      marginBottom: '1.2rem',
      color: '#2C5282',
    },
    p: {
      fontSize: '1.1rem',
      lineHeight: '1.8',
      color: '#4A5568',
      marginBottom: '1.5rem',
    },
    list: {
      fontSize: '1.1rem',
      lineHeight: '1.8',
      color: '#4A5568',
      marginBottom: '1.5rem',
    },
    listItem: {
      marginBottom: '1rem',
      paddingLeft: '1rem',
    },
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <>
      <Helmet>
        <title>DNACloudHub | About</title>
        <meta name="description" content="Learn more about DNACloudHub, the premier cloud-based platform designed to seamlessly run qpAdm models for advanced genetic analysis." />
        <meta property="og:title" content={'DNACloudHub | About'} />
        <meta property="og:description" content={'"Learn more about DNACloudHub, the premier cloud-based platform designed to seamlessly run qpAdm models for advanced genetic analysis."'} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://dnacloudhub.com/about`} />
      </Helmet>

      <Container fluid className="px-0" style={{ textAlign: 'left' }}>
        <motion.div
          style={AboutStyling.hero}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <img
            alt="about us"
            style={{ width: '100%', height: '500px', objectFit: 'cover' }}
            src={"./aboutmain.jpeg"}
          />
          <div style={AboutStyling.heroOverlay}>
            <h1 style={AboutStyling.heroTitle}>About DNACloudHub</h1>
            <p style={AboutStyling.heroSubtitle}>
              Empowering genetic research through advanced cloud computing
            </p>
          </div>
        </motion.div>

        <Container>
          <Row className="justify-content-center">
            <Col lg={10} style={AboutStyling.general}>
              <motion.section
                id="about-us"
                style={AboutStyling.section}
                {...fadeIn}
              >
                <p style={AboutStyling.p}>Welcome to DNACloudHub, the leading cloud-based platform offering a seamless, user-friendly service that hosts the infrastructure for running qpAdm models. We are committed to empowering genetic researchers, geneticists, and scientists by providing a powerful platform to run custom qpAdm models and perform advanced genetic analysis without the need for specialized hardware or complex setups.</p>

                <motion.div {...fadeIn}>
                  <h2 style={AboutStyling.h2}>What is DNACloudHub?</h2>
                  <p style={AboutStyling.p}>DNACloudHub is a state-of-the-art cloud platform that hosts the infrastructure needed for running qpAdm models. It allows researchers to effortlessly run their own custom models, analyze genetic data, and explore the genetic makeup of ancient and modern populations, all through a user-friendly interface. We focus on providing an accessible, scalable, and efficient platform that simplifies the process of running complex genetic analyses in the cloud.</p>
                </motion.div>

                <motion.div {...fadeIn}>
                  <h2 style={AboutStyling.h2}>Our Core Service</h2>
                  <p style={AboutStyling.p}>At DNACloudHub, we offer a comprehensive service that hosts the technical infrastructure required for running qpAdm. Instead of dealing with the complexities of setting up and maintaining specialized software and hardware, you can focus entirely on your research. Here's how our service works:</p>
                  <ol style={AboutStyling.list}>
                    <li style={AboutStyling.listItem}><strong>Custom Model Hosting</strong>: Users can upload and run their own custom qpAdm models on our secure and scalable cloud infrastructure.</li>
                    <li style={AboutStyling.listItem}><strong>Cloud-Based Execution</strong>: Our cloud platform allows you to run models from anywhere, without the need for local installations or high-performance computing systems.</li>
                    <li style={AboutStyling.listItem}><strong>Real-Time Results</strong>: Analyze genetic data and receive results instantly, speeding up your research and allowing for rapid iteration of hypotheses and models.</li>
                    <li style={AboutStyling.listItem}><strong>Scalable and Secure</strong>: Our platform ensures reliable, high-performance execution with robust security measures to protect your data.</li>
                  </ol>
                </motion.div>

                <motion.div {...fadeIn}>
                  <h2 style={AboutStyling.h2}>Why Choose DNACloudHub?</h2>
                  <p style={AboutStyling.p}>Our platform offers several key benefits that set us apart from traditional qpAdm tools:</p>
                  <ul style={AboutStyling.list}>
                    <li style={AboutStyling.listItem}><strong>Seamless Access</strong>: Run your custom models anytime, anywhere, with a simple web-based interface that removes technical barriers.</li>
                    <li style={AboutStyling.listItem}><strong>Fast, Scalable Infrastructure</strong>: Leverage the power of cloud computing for quick model execution, no matter the scale of your data or complexity of the analysis.</li>
                    <li style={AboutStyling.listItem}><strong>Cost-Efficient</strong>: No need for expensive hardware. Simply pay for the service you need, without the upfront costs of setting up your own infrastructure.</li>
                    <li style={AboutStyling.listItem}><strong>Customizability</strong>: Tailor your analyses to meet your specific research needs, whether you're studying ancient population migrations, admixture events, or genetic diversity.</li>
                    <li style={AboutStyling.listItem}><strong>Collaborative Tools</strong>: Collaborate with colleagues by sharing models and results easily, making teamwork across institutions or disciplines more efficient.</li>
                  </ul>
                </motion.div>

                <motion.div {...fadeIn}>
                  <h2 style={AboutStyling.h2}>Our Vision</h2>
                  <p style={AboutStyling.p}>At DNACloudHub, we aim to transform genetic research by making advanced tools like qpAdm accessible to everyone, regardless of their technical expertise. Our mission is to provide a platform that empowers researchers to run, refine, and scale their genetic analysis with ease, unlocking new insights into the complex history of human populations and genetics.</p>
                  <p style={AboutStyling.p}>We are proud to be at the forefront of making advanced genetic analysis tools like qpAdm available as a cloud-based service. Join us today to leverage our infrastructure and take your research to the next level!</p>
                </motion.div>
              </motion.section>
            </Col>
          </Row>
        </Container>
      </Container>
    </>
  );
}

export default About;
